import { criterion, fromCriteria } from "@/core/check";
import type { CheckResult, Lesson } from "@/core/types";
import { mitErsatz } from "@/office/makro/pruefung";
import { type Fensteransicht, readAnsicht } from "@/office/word/pruefen";
import { MAKRO_DOKUMENT, VBA_ANSICHT } from "../makros";

const NAMEN: Record<string, string> = {
  Print: "Drucklayout",
  Web: "Weblayout",
  Outline: "Gliederung",
  Normal: "Entwurf",
  Reading: "Lesemodus",
  PrintPreview: "Seitenansicht",
  Master: "Zentraldokument",
};

function pruefeAnsicht(bewerten: (ansicht: Fensteransicht) => CheckResult): Promise<CheckResult> {
  return mitErsatz(
    readAnsicht,
    MAKRO_DOKUMENT,
    (bericht) => ({
      typ: VBA_ANSICHT[bericht.zahl("ansicht") ?? 0] ?? null,
      navigationsbereich: bericht.janein("navigationsbereich"),
      formatierungszeichen: bericht.janein("formatierungszeichen"),
    }),
    bewerten,
  );
}

export const lektionAnsichten: Lesson = {
  id: "ansichten",
  title: "Die Ansichten von Word",
  summary: "Drucklayout, Gliederung, Lesemodus und Navigationsbereich – dasselbe Dokument, anders betrachtet.",
  durationMinutes: 9,
  learningGoals: [
    "Die Ansichten auf der Registerkarte „Ansicht“ unterscheiden",
    "In der Gliederungsansicht Kapitel umstellen",
    "Den Navigationsbereich zum Springen und Umsortieren nutzen",
  ],
  steps: [
    {
      id: "gliederungsansicht",
      title: "Die Gliederungsansicht",
      explanation: [
        "Die Registerkarte „Ansicht“ bietet fünf Arten, dasselbe Dokument zu betrachten. " +
          "Drucklayout ist der Standard und zeigt die Seiten wie gedruckt. Der Lesemodus " +
          "blendet alle Werkzeuge aus. Weblayout zeigt den Text ohne Seitenumbrüche. Entwurf " +
          "zeigt ihn ohne Ränder und Kopfzeilen – schnell bei sehr langen Dokumenten.",
        "Die Gliederungsansicht ist die mächtigste: Sie zeigt das Dokument als Baum der " +
          "Überschriften. Mit „Ebene anzeigen“ blendest du alles unterhalb von Ebene 2 aus, " +
          "und ein ganzes Kapitel samt Unterkapiteln und Text verschiebst du mit " +
          "Alt + Umschalt + Pfeil oben/unten.",
        "Das funktioniert nur, wenn die Überschriften Formatvorlagen tragen – der rote " +
          "Faden aus dem Modul „Formatvorlagen“ zahlt sich hier ein weiteres Mal aus.",
      ],
      keyPoints: [
        "Ansicht → Gliederung → Ebene anzeigen.",
        "Alt + Umschalt + Pfeil verschiebt ein Kapitel samt Inhalt.",
        "Ohne Überschriftenvorlagen gibt es keine Gliederung.",
      ],
      task: {
        instruction: "Wechsle in die Gliederungsansicht und lass dir nur die Ebenen 1 und 2 anzeigen.",
        hint: "Ansicht → Gliederung, dann oben bei „Ebene anzeigen“ „Ebene 2“ wählen.",
        macro: MAKRO_DOKUMENT,
        macroNurErsatz: true,
        check: () =>
          pruefeAnsicht(({ typ }) =>
            fromCriteria(
              [criterion(`Die Gliederungsansicht ist aktiv (aktuell: ${NAMEN[typ ?? ""] ?? typ ?? "unbekannt"})`, typ === "Outline")],
              {
                success: "Du siehst das Dokument als Baum – ideal, um Kapitel umzustellen.",
                failure: "Die Gliederungsansicht ist nicht aktiv.",
              },
            ),
          ),
      },
    },
    {
      id: "navigationsbereich",
      title: "Der Navigationsbereich",
      explanation: [
        "Der Navigationsbereich (Ansicht → Navigationsbereich, oder Strg + F) zeigt die " +
          "Überschriften links neben dem Dokument – im Drucklayout, ohne die Ansicht zu " +
          "wechseln. Ein Klick springt zum Kapitel; Ziehen verschiebt es samt Inhalt.",
        "Oben im selben Bereich steht das Suchfeld. Treffer erscheinen als Liste, und die " +
          "Überschriften mit Treffern werden hervorgehoben – eine schnelle Art, in einem " +
          "langen Dokument zu sehen, wo ein Begriff überall vorkommt.",
      ],
      keyPoints: [
        "Strg + F öffnet den Navigationsbereich.",
        "Überschriften ziehen = Kapitel verschieben.",
      ],
      task: {
        instruction: "Wechsle zurück ins Drucklayout und blende den Navigationsbereich ein.",
        hint: "Ansicht → Drucklayout, dann Ansicht → Häkchen bei „Navigationsbereich“.",
        macro: MAKRO_DOKUMENT,
        macroNurErsatz: true,
        check: () =>
          pruefeAnsicht(({ typ, navigationsbereich }) =>
            fromCriteria(
              [
                criterion(`Drucklayout ist aktiv (aktuell: ${NAMEN[typ ?? ""] ?? typ ?? "unbekannt"})`, typ === "Print"),
                criterion("Der Navigationsbereich ist eingeblendet", navigationsbereich === true),
              ],
              {
                success: "Seitenansicht und Inhaltsüberblick gleichzeitig.",
                failure: "Noch nicht ganz.",
              },
            ),
          ),
      },
    },
    {
      id: "fenster",
      title: "Zoom, Teilen und Nebeneinander",
      explanation: [
        "„Ansicht“ → „Zoom“ und der Regler unten rechts ändern nur die Darstellung, nie " +
          "das Dokument. „Seitenbreite“ passt die Seite ans Fenster an; „Mehrere Seiten“ " +
          "zeigt einen Überblick.",
        "„Teilen“ zeigt zwei Stellen desselben Dokuments übereinander – praktisch, um beim " +
          "Schreiben der Zusammenfassung die Einleitung im Blick zu haben. „Neues Fenster“ " +
          "und „Nebeneinander anzeigen“ vergleichen zwei Dokumente mit synchronem Scrollen.",
        "Und: Die Formatierungszeichen (¶ auf der Registerkarte „Start“, Strg + Umschalt + " +
          "+) machen sichtbar, was dieser Kurs immer wieder sucht – Leerzeilen, " +
          "Leerzeichen-Einzüge, manuelle Umbrüche.",
      ],
      keyPoints: [
        "Zoom ändert nur die Darstellung.",
        "Teilen = zwei Stellen desselben Dokuments.",
        "Nebeneinander anzeigen = zwei Dokumente mit synchronem Scrollen.",
      ],
    },
  ],
};
